import { Avatar, Tag } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import type { ColumnsType } from 'antd/es/table'
import type { SortOrder } from 'antd/es/table/interface'
import type { HTMLAttributes, TdHTMLAttributes, ThHTMLAttributes } from 'react'
import styled from 'styled-components'
import type {
  ChargeReportRow,
  ChargeReportSortField
} from '../types/chargeReport'

type Translate = (key: string, options?: Record<string, unknown>) => string

function getHeaderTestProps(
  dataColumn: string,
  className?: string
): HTMLAttributes<HTMLTableCellElement> &
  ThHTMLAttributes<HTMLTableCellElement> {
  return {
    'data-testid': 'charge-report-col-header',
    'data-column': dataColumn,
    ...(className ? { className } : {})
  } as HTMLAttributes<HTMLTableCellElement> &
    ThHTMLAttributes<HTMLTableCellElement>
}

function getCellTestProps(
  dataColumn: string
): HTMLAttributes<HTMLTableCellElement> &
  TdHTMLAttributes<HTMLTableCellElement> {
  return {
    'data-testid': 'charge-report-col-cell',
    'data-column': dataColumn
  } as HTMLAttributes<HTMLTableCellElement> &
    TdHTMLAttributes<HTMLTableCellElement>
}

export type GetChargeReportColumnsParams = {
  t: Translate
  nominative: string
  sortField: ChargeReportSortField
  sortDir: 1 | -1
  onNeedsAttentionClick: (row: ChargeReportRow) => void
}

export function getChargeReportColumns({
  t,
  nominative,
  sortField,
  sortDir,
  onNeedsAttentionClick
}: GetChargeReportColumnsParams): ColumnsType<ChargeReportRow> {
  const sortOrderFor = (key: ChargeReportSortField): SortOrder | undefined =>
    sortField === key ? (sortDir === 1 ? 'ascend' : 'descend') : undefined

  return [
    {
      title: <HeaderText weight="bold">{nominative}</HeaderText>,
      key: 'serviceUser',
      sortOrder: sortOrderFor('serviceUser'),
      sorter: true,
      width: 240,
      fixed: 'left',
      onHeaderCell: () => getHeaderTestProps('service-user'),
      onCell: () => getCellTestProps('service-user'),
      render: (_, record) => (
        <CellWithAvatar>
          <AvatarCell>
            <Avatar url={record.serviceUser.avatarUrl ?? undefined} size={22} />
          </AvatarCell>
          <CellPrimaryText>{record.serviceUser.name}</CellPrimaryText>
        </CellWithAvatar>
      )
    },
    {
      title: (
        <HeaderText weight="medium">
          {t('CHARGE_REPORT_COL_FUNDING_TYPE')}
        </HeaderText>
      ),
      key: 'fundingType',
      sortOrder: sortOrderFor('fundingType'),
      sorter: true,
      width: 180,
      onHeaderCell: () => getHeaderTestProps('funding-type'),
      onCell: () => getCellTestProps('funding-type'),
      render: (_, record) => (
        <CellPrimaryText>{record.fundingType?.name ?? '-'}</CellPrimaryText>
      )
    },
    {
      title: (
        <MultilineHeaderText weight="medium">
          {t('CHARGE_REPORT_COL_TOTAL_RECONCILED_CHARGE')}
        </MultilineHeaderText>
      ),
      key: 'totalReconciledCharge',
      sortOrder: sortOrderFor('totalReconciledCharge'),
      sorter: true,
      width: 180,
      onHeaderCell: () =>
        getHeaderTestProps(
          'total-reconciled-charge',
          'reconciliation-header-multiline'
        ),
      onCell: () => getCellTestProps('total-reconciled-charge'),
      render: (_, record) => (
        <CellPrimaryText weight="bold">
          {record.totalReconciledCharge.toFixed(2)}{' '}
          {t('RECONCILIATION_GBP_SUFFIX')}
        </CellPrimaryText>
      )
    },
    {
      title: (
        <MultilineHeaderText weight="medium">
          {t('CHARGE_REPORT_COL_NEEDS_ATTENTION')}
        </MultilineHeaderText>
      ),
      key: 'needsAttentionCount',
      sortOrder: sortOrderFor('needsAttentionCount'),
      sorter: true,
      width: 160,
      onHeaderCell: () =>
        getHeaderTestProps(
          'needs-attention',
          'reconciliation-header-multiline'
        ),
      onCell: () => getCellTestProps('needs-attention'),
      render: (_, record) =>
        record.needsAttentionCount === 0 ? (
          <Tag color="success">{t('CHARGE_REPORT_NEEDS_ATTENTION_ALL_OK')}</Tag>
        ) : (
          <NeedsAttentionTagWrap
            data-testid="charge-report-needs-attention-badge"
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onNeedsAttentionClick(record)
              }
            }}
            onClick={(e) => {
              e.stopPropagation()
              onNeedsAttentionClick(record)
            }}
          >
            <Tag color="orange">
              {t('CHARGE_REPORT_NEEDS_ATTENTION_EVENTS', {
                count: record.needsAttentionCount
              })}
            </Tag>
          </NeedsAttentionTagWrap>
        )
    },
    {
      title: (
        <MultilineHeaderText weight="medium">
          {t('CHARGE_REPORT_COL_RECONCILED_HOURS')}
        </MultilineHeaderText>
      ),
      key: 'reconciledHours',
      sortOrder: sortOrderFor('reconciledHours'),
      sorter: true,
      width: 140,
      onHeaderCell: () =>
        getHeaderTestProps(
          'reconciled-hours',
          'reconciliation-header-multiline'
        ),
      onCell: () => getCellTestProps('reconciled-hours'),
      render: (_, record) => (
        <CellPrimaryText>{record.reconciledHours}</CellPrimaryText>
      )
    }
  ]
}

const NeedsAttentionTagWrap = styled.span`
  cursor: pointer;
`

const CellPrimaryText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const HeaderText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const MultilineHeaderText = styled(Text)`
  white-space: pre-line;
  color: ${({ theme }) => theme.color.neutral.color3};
`

const CellWithAvatar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`

const AvatarCell = styled.div`
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  overflow: hidden;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;

  .ant-avatar {
    width: 22px !important;
    height: 22px !important;
    line-height: 22px !important;
    flex-shrink: 0;
  }

  .ant-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`
